import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import QuoteSection from '../components/QuoteSection';
import LightboxModal from '../components/LightboxModal';
import { companyInfo } from '../data/siteData';
import { imageUrl } from '../lib/images';

export default function TerrasserPage() {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const galleryImages = [
    {
      src: "/_assets/glashegn.webp",
      title: "Terrasse med glasafskærmning",
      category: "Glasafskærmning",
      desc: "Terrasseløsning med klart glashegn, der skærmer for vinden uden at tage udsigten."
    },
    {
      src: "/_assets/udestue.webp",
      title: "Overdækning og terrasseområde",
      category: "Overdækket terrasse",
      desc: "Integreret udestue og terrasse med lyst tag og adgang til haven."
    },
    {
      src: "/_assets/485170711-1308137817341082-5036954073090041011-n-0.jpg",
      title: "Hegn og afskærmning ved have/terrasse",
      category: "Afskærmning",
      desc: "Robust afskærmning udført i holdbare materialer omkring privat terrasse."
    },
    {
      src: "/_assets/sommerhus-betonhegn-skydeport.webp",
      title: "Terrasseafgrænsning og hegn",
      category: "Have & Fritid",
      desc: "Afskærmning og læhegn opført ved sommerhusgrund."
    }
  ];

  return (
    <>
      <SEO
        title="Terrasser | HOVENDALS ApS"
        description="Terrasseløsninger med fokus på funktionalitet, kvalitet og et flot udtryk. Få et uforpligtende tilbud på din terrasse i hele Danmark."
        ogImage="/_assets/glashegn.webp"
      />

      {/* Hero Section - Type B Full width visual surface */}
      <section className="relative min-h-[500px] lg:min-h-[600px] flex items-center bg-[#143463] overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={imageUrl("/_assets/glashegn.webp", "width=1920,quality=85,format=auto")}
            alt="Terrasseløsning med glasafskærmning"
            className="w-full h-full object-cover opacity-45"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#143463]/90 via-[#143463]/60 to-transparent"></div>
        </div>

        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-24 relative z-10 w-full">
          <div className="max-w-2xl space-y-6">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#C2CCDF]">
              Uderum & Afskærmning
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white tracking-tight leading-[1.1]">
              Terrasser
            </h1>
            <p className="text-lg sm:text-xl text-slate-200 font-light leading-relaxed">
              Terrasseløsninger med fokus på funktionalitet, kvalitet og et flot udtryk.
            </p>
            <div className="pt-2 flex flex-wrap gap-4">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#004FA4] hover:bg-[#183E7F] text-white font-medium text-xs tracking-wider uppercase transition-colors"
              >
                <span>Indhent tilbud</span>
                <i className="fa-sharp fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
              </Link>
              <a
                href={`tel:${companyInfo.phoneFormatted}`}
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-medium text-xs tracking-wider uppercase transition-colors"
              >
                <i className="fa-sharp fa-solid fa-phone text-[#C2CCDF]" aria-hidden="true"></i>
                <span>Ring {companyInfo.phone}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 1: Introduktion (Type A: Stort billede med tekst ved siden af) */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            
            {/* Tekst (35-45%) */}
            <div className="lg:col-span-5 space-y-6">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#004FA4]">
                Kvalitet til uderummet
              </span>
              <h2 className="text-3xl sm:text-4xl font-light text-[#143463] tracking-tight leading-snug">
                Skræddersyede løsninger til din terrasse
              </h2>
              <div className="space-y-4 text-base text-[#4B515D] font-light leading-relaxed">
                <p>
                  Terrassen er samlingspunktet for haven i forårs- og sommermånederne. Hos HOVENDALS arbejder vi med gennemtænkte uderumsløsninger, hvor afskærmning, hegn og integration til haven skaber et funktionelt og æstetisk helhedsindtryk.
                </p>
                <p>
                  Hvad enten du ønsker glasafskærmning, der bevarer udsigten og giver læ for vinden, eller solide læhegn i komposit eller træ til terrassens sider, rådgiver vi fra start til slut.
                </p>
                <p>
                  Vi leverer og monterer med præcision i hele landet.
                </p>
              </div>

              <div className="pt-2">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#004FA4] hover:text-[#183E7F] group"
                >
                  <span>Kontakt os for en vurdering af din opgave</span>
                  <i className="fa-sharp fa-solid fa-arrow-right transition-transform group-hover:translate-x-1" aria-hidden="true"></i>
                </Link>
              </div>
            </div>

            {/* Billede (55-65%) */}
            <div className="lg:col-span-7">
              <div className="relative overflow-hidden bg-[#F3F4F7] border border-[#D6D3CE]/60">
                <img
                  src={imageUrl("/_assets/glashegn.webp", "width=1200,quality=85,format=auto")}
                  alt="Terrasse med glasafskærmning"
                  className="w-full h-auto aspect-[16/10] object-cover"
                />
                <div className="p-4 bg-white/95 border-t border-[#D6D3CE]/40">
                  <span className="text-xs font-medium text-[#143463]">Projekt:</span>
                  <span className="text-xs text-[#4B515D] ml-2 font-light">Terrasse med glasafskærmning og frit udsyn over landskabet</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Sektion 2: Projektpræsentation / Billedgalleri */}
      <section className="py-20 md:py-28 bg-[#F3F4F7] border-t border-b border-[#D6D3CE]/60">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#004FA4]">
              Udvalgte Projekter
            </span>
            <h2 className="text-3xl sm:text-4xl font-light text-[#143463] tracking-tight mt-2">
              Inspiration til terrasse & afskærmning
            </h2>
            <p className="text-[#4B515D] font-light text-base mt-2">
              Se udvalgte eksempler på afskærmning, glasløsninger og overdækninger udført til private ejendomme.
            </p>
          </div>

          {/* Grid af store produkt- og projektfotos */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {galleryImages.map((img, idx) => (
              <div
                key={img.src}
                onClick={() => setLightboxIndex(idx)}
                className="group cursor-pointer bg-white border border-[#D6D3CE]/60 overflow-hidden"
              >
                <div className="relative overflow-hidden aspect-[16/10] bg-[#F3F4F7]">
                  <img
                    src={imageUrl(img.src, "width=900,quality=85,format=auto")}
                    alt={img.title}
                    className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]"
                  />
                  <div className="absolute top-4 left-4 bg-[#143463]/90 text-white text-[11px] font-medium px-3 py-1 uppercase tracking-wider">
                    {img.category}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-light text-[#143463] group-hover:text-[#004FA4] transition-colors flex items-center justify-between">
                    <span>{img.title}</span>
                    <i className="fa-sharp fa-solid fa-arrow-right text-xs text-[#004FA4] transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true"></i>
                  </h3>
                  <p className="text-xs text-[#4B515D] font-light mt-2 leading-relaxed">
                    {img.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sektion 3: Kontakt / CTA */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#143463] text-white p-10 sm:p-16 flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="max-w-2xl space-y-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#C2CCDF]">
                Klar til et uforpligtende tilbud?
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light text-white tracking-tight">
                Lad os hjælpe dig med din nye terrasseløsning
              </h2>
              <p className="text-sm sm:text-base text-slate-300 font-light leading-relaxed">
                Kontakt Simon Hovendal direkte eller udfyld formularen for en personlig opmåling og tilbud.
              </p>
            </div>
            <div className="shrink-0 flex flex-col sm:flex-row gap-3">
              <Link
                to="/kontakt"
                className="px-8 py-4 bg-[#004FA4] hover:bg-[#183E7F] text-white font-medium text-xs tracking-wider uppercase transition-colors text-center"
              >
                Indhent tilbud
              </Link>
              <a
                href={`tel:${companyInfo.phoneFormatted}`}
                className="px-8 py-4 bg-transparent border border-white/40 hover:border-white text-white font-medium text-xs tracking-wider uppercase transition-colors text-center"
              >
                Ring {companyInfo.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      <QuoteSection />

      {lightboxIndex !== null && (
        <LightboxModal
          images={galleryImages}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={(newIdx) => setLightboxIndex(newIdx)}
        />
      )}
    </>
  );
}
